import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarLightReachReaders } from "./solar-price-dependents"
import type {
  SolarGreenButtonApplyRequest,
  SolarProposalConsumptionDto,
  SolarProposalGreenButtonApplyDto,
} from "../dto/solar-proposal-consumption.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type ApplySolarProposalGreenButtonVariables =
  SolarGreenButtonApplyRequest & {
    proposalId: string
    siteId: string
  }

/**
 * Solar V2's Green Button switch, resume and confirm. The gateway changes only
 * the consumption selection: nothing recalculates, so there is no
 * recalculation state to wait for. The answer is the consumption read, which
 * replaces that cache entry; the header, design, savings and utility reads
 * refetch so savings report out of date, and the drawer's Site and selection
 * reads refetch so it shows the application.
 */
export default function useApplySolarProposalGreenButton(): UseMutationResult<
  SolarProposalGreenButtonApplyDto,
  unknown,
  ApplySolarProposalGreenButtonVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, siteId: _siteId, ...request }) =>
      solarProposalService.postGreenButton(proposalId, request),
    onSuccess: (
      { pendingImport: _pendingImport, ...consumption },
      { proposalId, siteId },
    ) => {
      queryClient.setQueryData<SolarProposalConsumptionDto>(
        queryKeys.solarProposal.consumption(proposalId),
        consumption,
      )
      for (const queryKey of [
        queryKeys.solarProposal.byId(proposalId),
        queryKeys.solarProposal.design(proposalId),
        queryKeys.solarProposal.savings(proposalId),
        queryKeys.solarProposal.share(proposalId),
        queryKeys.solarProposal.utility(proposalId),
        queryKeys.solarProposal.contract(proposalId),
        queryKeys.proposalConsumption.selection(proposalId),
      ]) {
        void queryClient.invalidateQueries({ queryKey, exact: true })
      }
      // Savings out of date hold Get LightReach pricing.
      invalidateSolarLightReachReaders(queryClient, proposalId)
      // Prefix matches: per-import insights and the Site's per-proposal reads.
      for (const queryKey of [
        queryKeys.proposalConsumption.insights(proposalId),
        queryKeys.arcadiaGreenButton.site(siteId),
      ]) {
        void queryClient.invalidateQueries({ queryKey })
      }
    },
    // A lock or newer data that appeared changes what the drawer may offer,
    // and the Site read stops polling once settled, so a newer import shows
    // only when it is read again.
    onError: (_error, { proposalId, siteId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.arcadiaGreenButton.site(siteId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.consumption(proposalId),
        exact: true,
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.proposalConsumption.selection(proposalId),
        exact: true,
      })
    },
  })
}
